import React, { createContext, useContext, useReducer, useEffect, useRef } from "react"

import { connect, useDispatch } from "react-redux"
import { useParams, useLocation } from "react-router-dom"

import { getTeeupById } from "../../../actions/activeTeeupActions"
import { markTeeupNotificationsReadOnOpenThunk } from "../../../actions/notificationActions"
import { selectActiveTeeupId } from "../../../selectors/activeTeeup"

import { TeeUpPageReducer } from "./reducer"

const TeeUpPageStateContext = createContext({})
const TeeUpPageDispatchContext = createContext(undefined)

const TeeUpPageProvider = props => {
  const { children, activeTeeupId } = props
  const [state, dispatch] = useReducer(TeeUpPageReducer, {
    interactionArea: null, // area that opens for example when suggestions are clicked. it pushes the conversation to the right, and right side area gets closed.
    openPeopleArea: false,
    // isSuggestWhenModalOpen: false,
    // isSuggestWhereModalOpen: false,
    isThreeDotsDropDownOpen: false,
  })
  const param = useParams()
  const location = useLocation()
  // NOTE: `dispatch` above (from useReducer) is this component's own local-context reducer
  // dispatch (fed to TeeUpPageDispatchContext, consumed via useTeeUpPageDispatch/useTeeUpPage) --
  // NOT redux. Named distinctly to avoid shadowing it.
  const reduxDispatch = useDispatch()
  // Belt-and-suspenders: prevent a second fetch from starting while one is already in flight.
  // Primary fix is in TeeUpsNd (removing the setTeeupActive(initialTeeup) call from the shared
  // useFetchTeeUps hook). This ref guard is a secondary safety net.
  const fetchInFlight = useRef(false)

  // fetch teeup by id on teeup overview load
  useEffect(() => {
    // C1 fix (2026-07-12): only skip the fetch when the ALREADY-active teeup IS the one this
    // route wants -- `if (activeTeeupId) return` used to skip on ANY truthy activeTeeupId,
    // which is why switching TeeUps via the notification bell (component instance persists
    // across the route change, unlike the sidebar-card path) kept rendering the PREVIOUS
    // TeeUp's data while writes (decide/respond/message) went to the new, correct route
    // teeupId -- proven live: a marker chat message POSTed to TeeUp A's uuid while the URL/nav
    // said TeeUp B landed in A. String() coercion on both sides: param.id is always a string
    // (React Router route param); activeTeeupId (state.activeTeeup.id, see
    // selectors/activeTeeup.js:15-18) may be a number -- without coercion a same-teeup
    // string/number mismatch would cause a harmless EXTRA fetch (not a bug) but coercion keeps
    // this guard's intent exact.
    if (activeTeeupId && String(activeTeeupId) === String(param.id)) return
    // Secondary guard: don't start a new fetch if one is already in flight.
    // Primary fix: setTeeupActive(initialTeeup) was removed from useFetchTeeUps (moved to
    // TeeUpsNd) so TeeupPageContent no longer resets activeTeeup.id on mount.
    if (fetchInFlight.current) return

    if (param.id && location.pathname.includes("teeUps")) {
      fetchInFlight.current = true
      getTeeupById(param.id).finally(() => {
        fetchInFlight.current = false
      })
    }
  }, [param.id, location.pathname, activeTeeupId])

  // FIX-1 (inbox unread-dot, 2026-07-04): clear THIS TeeUp's unread notifications on every detail
  // open (iPhone parity), independent of the fetch-guard effect above -- this must fire on every
  // navigation to a TeeUp, not just the first time activeTeeupId is empty (e.g. re-opening a TeeUp
  // already cached from a prior visit still needs its dot cleared). Fire-and-forget, never blocks
  // render.
  useEffect(() => {
    if (param.id && location.pathname.includes("teeUps")) {
      reduxDispatch(markTeeupNotificationsReadOnOpenThunk(param.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [param.id, location.pathname])

  return (
    <TeeUpPageStateContext.Provider value={state}>
      <TeeUpPageDispatchContext.Provider value={dispatch}>
        {children}
      </TeeUpPageDispatchContext.Provider>
    </TeeUpPageStateContext.Provider>
  )
}

const useTeeUpPageState = () => {
  const context = useContext(TeeUpPageStateContext)

  if (context === undefined) {
    throw new Error("useTeeUpPageState must be used within a TeeUpPageProvider")
  }
  return context
}

const useTeeUpPageDispatch = () => {
  const context = useContext(TeeUpPageDispatchContext)

  if (context === undefined) {
    throw new Error(
      "useTeeUpPageDispatch must be used within a TeeUpPageProvider"
    )
  }
  return context
}

const mapStateToProps = state => {
  const activeTeeupId = selectActiveTeeupId(state)

  return {
    activeTeeupId,
  }
}

export default connect(mapStateToProps)(TeeUpPageProvider)

export { TeeUpPageProvider, useTeeUpPageState, useTeeUpPageDispatch }
